import React, { useState, useEffect } from 'react';
import { Phone, Instagram, Menu, X, ArrowRight, Calendar, Clock, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// JSON-LD script breakout'unu engellemek için `<` karakterini escape et
const safeJsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

const NAV = [
  { href: '/#hizmetler', label: 'Hizmetler' },
  { href: '/#organizasyonlar', label: 'Organizasyonlar' },
  { href: '/blog', label: 'Blog' },
  { href: '/#hakkimizda', label: 'Hakkımızda' },
  { href: '/#iletisim', label: 'İletişim' },
];

const PublicHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/95 backdrop-blur-md shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3">
            <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-12 w-12 object-contain rounded" />
            <div className="leading-tight text-slate-900">
              <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
              <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-75">SPORTİF FAALİYETLER</p>
            </div>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {NAV.map(item => (
              <a key={item.href} href={item.href} className="text-sm font-medium tracking-wide text-slate-700 hover:text-[#0052A5] transition-colors">{item.label}</a>
            ))}
            <a href="/#iletisim" className="bg-[#0052A5] hover:bg-[#003D7A] text-white px-5 py-2.5 text-sm font-semibold tracking-wide" style={{ fontFamily: 'Outfit' }}>BİZE ULAŞIN</a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-slate-900">{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </div>
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-6 py-4 space-y-3">
            {NAV.map(item => (<a key={item.href} href={item.href} onClick={() => setMenuOpen(false)} className="block py-2 text-slate-700 hover:text-[#0052A5] font-medium">{item.label}</a>))}
          </div>
        </div>
      )}
    </nav>
  );
};

const PublicFooter = () => (
  <footer className="bg-black text-white relative overflow-hidden">
    <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[20vw] font-black tracking-tighter text-white/[0.03] pointer-events-none select-none whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
    <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <img src="/avrasya-logo.jpg" alt="Avrasya Sportif" className="h-10 w-10 object-contain rounded" />
            <div className="leading-tight">
              <p className="text-base font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>AVRASYA</p>
              <p className="text-[10px] tracking-[0.25em] -mt-0.5 opacity-50">SPORTİF FAALİYETLER</p>
            </div>
          </div>
          <p className="text-white/50 text-sm max-w-md">Kurumsal etkinlikler, voleybol turnuvaları ve profesyonel canlı yayın çözümleri.</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">Menü</p>
          <ul className="space-y-2 text-sm">{NAV.map(item => (<li key={item.href}><a href={item.href} className="text-white/70 hover:text-white">{item.label}</a></li>))}</ul>
        </div>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-3">İletişim</p>
          <ul className="space-y-2 text-sm">
            <li><a href="tel:+905053686091" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> 0505 368 60 91</a></li>
            <li><a href="https://instagram.com/avrasyasportif" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white inline-flex items-center gap-2"><Instagram className="w-3.5 h-3.5" /> @avrasyasportif</a></li>
          </ul>
        </div>
      </div>
      <div className="pt-6 border-t border-white/10 text-xs text-white/40">© {new Date().getFullYear()} Avrasya Sportif Faaliyetler. Tüm hakları saklıdır.</div>
    </div>
  </footer>
);

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return ''; }
};

const readingTime = (content) => {
  const words = (content || '').split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

export const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Blog | Avrasya Sportif — Şirket İçi Etkinlik & Canlı Yayın Yazıları';
    axios.get(`${API}/blog/posts`).then(r => setPosts(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }} data-testid="blog-list-page">
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <PublicHeader />

      <section className="pt-32 pb-12 bg-gradient-to-br from-[#0052A5] via-[#003D7A] to-[#001E3C] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <p className="text-white/60 font-bold text-sm tracking-[0.3em] mb-3">— BLOG</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black uppercase leading-[0.95]" style={{ fontFamily: 'Outfit' }}>
            Sporda<br />Profesyonel<br /><span className="text-[#FF3B30]">Yazılar</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl">Şirket içi spor etkinliklerinden canlı yayın çözümlerine, voleybol turnuvası organizasyonundan kurumsal etkinlik rehberlerine — sporda profesyonelliğin perde arkası.</p>
        </div>
      </section>

      <section className="py-16 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          {loading ? (
            <p className="text-center py-20 text-slate-400">Yükleniyor...</p>
          ) : posts.length === 0 ? (
            <p className="text-center py-20 text-slate-400">Henüz yazı yok</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {posts.map(p => (
                <a key={p.slug} href={`/blog/${p.slug}`} className="group bg-white border border-slate-200 hover:border-[#0052A5] hover:shadow-2xl transition-all overflow-hidden flex flex-col" data-testid={`blog-card-${p.slug}`}>
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-200">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(p.created_at)}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{readingTime(p.excerpt)} dk okuma</span>
                    </div>
                    <h2 className="text-lg font-black uppercase tracking-tight leading-tight mb-3 text-slate-900 group-hover:text-[#0052A5] transition-colors" style={{ fontFamily: 'Outfit' }}>{p.title}</h2>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>
                    <div className="mt-auto inline-flex items-center gap-1 text-[#0052A5] text-sm font-semibold uppercase tracking-wide">
                      Devamını Oku <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

const renderMarkdown = (md) => {
  // Lightweight markdown → HTML (h2, h3, h4, p, ul, ol, strong, em)
  const lines = md.split('\n');
  const out = [];
  let inList = null;
  let listType = null;
  const flushList = () => {
    if (inList) {
      out.push(`</${listType}>`);
      inList = null; listType = null;
    }
  };
  const inline = (s) =>
    s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
     .replace(/\*([^*]+)\*/g, '<em>$1</em>')
     .replace(/`([^`]+)`/g, '<code>$1</code>');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith('#### ')) { flushList(); out.push(`<h4>${inline(line.slice(5))}</h4>`); continue; }
    if (line.startsWith('### ')) { flushList(); out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }
    if (line.startsWith('## ')) { flushList(); out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
    if (line.startsWith('# ')) { flushList(); out.push(`<h1>${inline(line.slice(2))}</h1>`); continue; }
    if (/^\d+\.\s/.test(line)) {
      if (inList && listType !== 'ol') flushList();
      if (!inList) { out.push('<ol>'); inList = true; listType = 'ol'; }
      out.push(`<li>${inline(line.replace(/^\d+\.\s/, ''))}</li>`);
      continue;
    }
    if (line.startsWith('- ')) {
      if (inList && listType !== 'ul') flushList();
      if (!inList) { out.push('<ul>'); inList = true; listType = 'ul'; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p>${inline(line)}</p>`);
  }
  flushList();
  return out.join('\n');
};

export const BlogArticlePage = ({ slug }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} | Avrasya Sportif Blog`;
        // Update meta description for SEO
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', r.data.excerpt);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-400">Yükleniyor...</p>
      </div>
    );
  }
  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }}>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <PublicHeader />
        <div className="pt-40 pb-20 text-center">
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Outfit' }}>Yazı Bulunamadı</h1>
          <a href="/blog" className="text-[#0052A5] underline">Blog'a Dön</a>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Manrope", system-ui, sans-serif' }} data-testid="blog-article-page">
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800;900&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <PublicHeader />

      {/* JSON-LD Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.cover_image,
          "datePublished": post.created_at,
          "dateModified": post.updated_at,
          "author": { "@type": "Organization", "name": "Avrasya Sportif Faaliyetler" },
          "publisher": {
            "@type": "Organization",
            "name": "Avrasya Sportif Faaliyetler",
            "logo": { "@type": "ImageObject", "url": "https://avrasyasportif.com/avrasya-logo.jpg" }
          },
          "mainEntityOfPage": `https://avrasyasportif.com/blog/${post.slug}`,
          "keywords": (post.keywords || []).join(', ')
        }) }}
      />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 sm:pb-32 text-white overflow-hidden"
        style={{
          backgroundImage: post.cover_image
            ? `linear-gradient(135deg, rgba(0,30,60,0.85) 0%, rgba(0,82,165,0.7) 100%), url(${post.cover_image})`
            : 'linear-gradient(135deg, #001E3C 0%, #0052A5 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative max-w-4xl mx-auto px-6 sm:px-12 lg:px-16">
          <a href="/blog" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Blog'a Dön
          </a>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-white/60 mb-4">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(post.created_at)}</span>
            <span>•</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{readingTime(post.content)} dk okuma</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black uppercase leading-[1.05]" style={{ fontFamily: 'Outfit' }}>
            {post.title}
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-3xl leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Content */}
      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-16">
          <div
            className="prose-blog"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(post.content)) }}
          />

          {(post.keywords && post.keywords.length > 0) && (
            <div className="mt-16 pt-8 border-t border-slate-200">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Etiketler</p>
              <div className="flex flex-wrap gap-2">
                {post.keywords.map((k, i) => (
                  <span key={i} className="bg-slate-100 hover:bg-[#0052A5] hover:text-white transition-colors px-3 py-1 text-xs">{k}</span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-[#0052A5] text-white p-8 text-center">
            <h3 className="text-2xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit' }}>
              Bir Sonraki Organizasyonunuz İçin <span className="italic font-light">hazırız</span>
            </h3>
            <p className="text-white/80 mb-5 text-sm">Şirketinizin spor etkinliği, canlı yayın hizmeti veya voleybol turnuvası için bize ulaşın.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href="tel:+905053686091" className="inline-flex items-center gap-2 bg-white text-[#0052A5] px-6 py-3 text-sm font-bold tracking-wider" style={{ fontFamily: 'Outfit' }}>
                <Phone className="w-4 h-4" /> 0505 368 60 91
              </a>
              <a href="https://instagram.com/avrasyasportif" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-6 py-3 text-sm font-bold tracking-wider hover:bg-white/10" style={{ fontFamily: 'Outfit' }}>
                <Instagram className="w-4 h-4" /> @avrasyasportif
              </a>
            </div>
          </div>
        </div>

        <style>{`
          .prose-blog { color: #1f2937; line-height: 1.75; font-size: 1.05rem; }
          .prose-blog h2 { font-family: 'Outfit', sans-serif; font-weight: 800; font-size: 1.875rem; color: #0F172A; margin-top: 3rem; margin-bottom: 1rem; line-height: 1.2; letter-spacing: -0.02em; }
          .prose-blog h3 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.5rem; color: #0F172A; margin-top: 2.5rem; margin-bottom: 0.75rem; }
          .prose-blog h4 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.15rem; color: #0052A5; margin-top: 2rem; margin-bottom: 0.5rem; }
          .prose-blog p { margin: 1rem 0; }
          .prose-blog ul, .prose-blog ol { margin: 1rem 0; padding-left: 1.5rem; }
          .prose-blog li { margin: 0.5rem 0; }
          .prose-blog ul li { list-style: disc; }
          .prose-blog ol li { list-style: decimal; }
          .prose-blog strong { color: #0052A5; font-weight: 700; }
          .prose-blog code { background: #F1F5F9; padding: 2px 6px; border-radius: 3px; font-size: 0.9em; }
        `}</style>
      </article>

      <PublicFooter />
    </div>
  );
};
